import { useAccountStore } from "@/composables/account/useAccountStore";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import {
  forwardsApi,
  FEASIBILITY_BLOCK_ERROR_TYPE,
  forwardsErrorMessage,
  forwardsErrorType,
  isDomainError,
  isNotFoundError,
} from "@/forwards2/forwardsApi";
import type {
  ListingWrite,
  MandateCommitmentRow,
  MandateDetailResponse,
  MandateDeviceRow,
  MandateListingKind,
  MandateMemberRow,
  MandatePreviewResponse,
} from "@/forwards2/forwardsTypes";
import { isForwardsStaff } from "@/forwards2/forwardsGuard";
import type { PeriodWidth } from "@/forwards2/periodUtils";
import { defaultMandateName } from "@/forwards2/mandates/mandateDefaultName";
import {
  blankMandateForm,
  cloneForm,
  formFromMandate,
  listingFromForm,
  productionRangeOf,
  requestFromForm,
  selectedWidth,
  type MandateEditorForm,
} from "@/forwards2/mandates/mandateEditorForm";
import {
  listingRowErrors,
  minimumPriceError,
  productionRangeError,
} from "@/forwards2/mandates/mandateFormRules";
import { debounce, isEqual, sortBy, uniqBy } from "lodash-es";
import {
  computed,
  onScopeDispose,
  reactive,
  readonly,
  ref,
  shallowRef,
  watch,
} from "vue";

/** The editor previews after this quiet period; long enough not to fire per keystroke. */
export const PREVIEW_DEBOUNCE_MS = 400;

export type MandateEditor = ReturnType<typeof useMandateEditor>;

/**
 * The mandate editor's state: one form, the live preview of it, and the writes.
 *
 * `PUT /mandates/<id>` replaces the mandate wholesale, so the form always holds the complete description
 * and every save sends all of it — a body missing the listing policy or `member_company_ids` would
 * delete what it left out. Nothing here throws: every call records an error the view renders inline.
 * A domain error is a sentence the view renders itself; anything else is a bug or an outage, and
 * also goes to the app-wide handler, which shows the correlation code.
 *
 * One editing session's state, so `MandateEditorModal` calls it in its setup with the mandate it opens
 * (`null` for a new one): the state and its timers go with the modal. Returned as one reactive
 * object, so a template reads `editor.loading` rather than a ref.
 */
export function useMandateEditor(openedMandateId: number | null) {
  const { globalErrorHandler } = useGlobalHandlers();
  // Changes only when a new mandate's create answers with its id.
  const mandateId = ref<number | null>(openedMandateId);
  const form = ref<MandateEditorForm>(blankMandateForm());
  const detail = ref<MandateDetailResponse | null>(null);
  const commitments = ref<MandateCommitmentRow[]>([]);
  const preview = ref<MandatePreviewResponse | null>(null);
  const savedForm = ref<MandateEditorForm>(blankMandateForm());
  const candidateDevices = ref<MandateDeviceRow[]>([]);
  // From a membership change, through its debounce, until the latest request answers.
  const devicesLoading = ref(false);
  const candidateCompanies = ref<MandateMemberRow[]>([]);

  const loading = ref(openedMandateId !== null);
  const previewLoading = ref(false);
  // Set from the form change until the debounced preview starts, so nothing judges the form on the
  // previous preview's blockers in between.
  const previewPending = ref(false);
  const saving = ref(false);
  const busyAction = ref<"delete" | "archive" | "unarchive" | null>(null);
  // `shallowRef`: a foreign error object put in a deep `ref` comes back as a reactive proxy, which
  // breaks identity checks and `instanceof` on it (C-09b-11).
  const loadError = shallowRef<unknown>(null);
  const notFound = ref(false);
  /**
   * Whether the name is the user's own. A new mandate's name follows the one generated from its
   * companies and devices until `setName` records its creator's; an existing mandate's name always is.
   */
  const nameEdited = ref(openedMandateId !== null);
  const saveError = shallowRef<unknown>(null);
  const candidateCompaniesError = shallowRef<unknown>(null);
  const warnings = ref<string[]>([]);

  const isNew = computed(() => mandateId.value === null);
  // What a save would send: entries left in a mode that is not selected are never saved, so they are
  // not unsaved changes either.
  const isDirty = computed(
    () =>
      !isEqual(requestFromForm(form.value), requestFromForm(savedForm.value))
  );
  const listingChanged = computed(
    () =>
      !isEqual(listingFromForm(form.value), listingFromForm(savedForm.value))
  );
  const entryWidth = computed(() => selectedWidth(form.value));

  /** The selected width's recurring template; the other widths keep theirs. */
  const recurringDraft = computed(() => form.value.recurring[entryWidth.value]);

  /** The selected width's one-off rows; the other widths keep theirs. */
  const oneOffListings = computed({
    get: () => form.value.one_off[entryWidth.value],
    set: (listings: ListingWrite[]) => {
      form.value.one_off[entryWidth.value] = listings;
    },
  });
  const capabilities = computed(() => detail.value?.capabilities ?? null);
  const canEdit = computed(() =>
    isNew.value ? true : (capabilities.value?.can_edit ?? false)
  );

  /**
   * A mandate's companies change only as an account that owns them (user, 2026-10-20), so a super admin
   * acting as themselves edits the rest of a client's mandate with its companies read-only.
   */
  const membersLocked = computed(
    () =>
      !isNew.value &&
      canEdit.value &&
      !(capabilities.value?.can_change_members ?? false)
  );

  const accountStore = useAccountStore();
  /** A new mandate's companies are offered from a super admin's own account until they manage a client's. */
  const offersOwnCompaniesOnly = computed(
    () =>
      isNew.value &&
      isForwardsStaff(accountStore.account) &&
      !accountStore.isManagingAccount
  );
  const blockers = computed(() => preview.value?.blockers ?? []);
  const previewWarnings = computed(() => preview.value?.warnings ?? []);

  /** A floor the write routes refuse — below zero, or finer than they store; the options card says which beside the field. */
  const minimumPriceProblem = computed(() =>
    minimumPriceError(form.value.minimum_price)
  );

  /** A production range the write routes refuse; a one-off listing sends none, so it has none. */
  const productionRangeProblem = computed(() =>
    form.value.listing_kind === "one_off"
      ? null
      : productionRangeError(productionRangeOf(form.value))
  );

  /**
   * Whether the form states something the write routes would refuse outright.
   *
   * The listing section writes what the user typed into the form — a period the mandate cannot state,
   * the same period on two rows, a production range that ends before it starts — and so does the
   * price field for a floor the routes refuse, so that each can explain the mistake beside its
   * field.
   * Such a form is not worth previewing (the route answers 400, and the editor would lose the
   * estimates it is showing) and must not be saved, so `refreshPreview` and `canSave` both stand
   * down while it is true.
   */
  const formInvalid = computed(() => {
    if (minimumPriceProblem.value !== null) return true;
    if (productionRangeProblem.value !== null) return true;
    if (form.value.listing_kind !== "one_off") return false;
    return listingRowErrors(oneOffListings.value).size > 0;
  });

  /** What a former member left behind here; non-null means this is the escrow-only view of the mandate. */
  const escrowRemaining = computed(
    () => detail.value?.escrow_remaining ?? null
  );

  /** Whether the form names a different membership than the mandate was saved with. */
  const membershipEdited = computed(
    () =>
      !isNew.value &&
      !isEqual(
        sortedIds(form.value.member_company_ids),
        sortedIds(detail.value?.mandate.member_company_ids ?? [])
      )
  );

  /**
   * The device panel's rows: the saved mandate's own devices, or the candidates of the companies the
   * form names whenever that membership is not the saved one — a company added in this edit has to
   * contribute its devices to the allowlist, or an allowlist mandate would need two saves
   * (RV2-mandate-lifecycle-08).
   */
  const devices = computed<MandateDeviceRow[]>(() =>
    isNew.value || membershipEdited.value
      ? candidateDevices.value
      : (detail.value?.devices ?? [])
  );

  /**
   * The devices the mandate itself sells from, derived from the form the way
   * `mandate_device_service.effective_device_ids_for` derives them on the backend, so the eligibility
   * card counts each blocker against the mandate's own devices rather than against every candidate of
   * its member companies (D-FIX5-1-01). Allowlist mode is explicit: an empty allowlist covers nothing.
   */
  const mandateDevices = computed<MandateDeviceRow[]>(() => {
    if (form.value.device_scope !== "allowlist") return devices.value;
    const allowed = new Set(form.value.allowlist_device_ids);
    return devices.value.filter((device) => allowed.has(device.device_id));
  });

  // Existing members must remain visible when the form replaces the entire membership.
  const memberCandidates = computed<MandateMemberRow[]>(() =>
    sortBy(
      uniqBy(
        [...candidateCompanies.value, ...(detail.value?.members ?? [])],
        (candidate) => candidate.company_id
      ),
      (candidate) => candidate.company_name.toLowerCase()
    )
  );

  const memberNames = computed(() => {
    const names = new Map(
      memberCandidates.value.map((candidate) => [
        candidate.company_id,
        candidate.company_name,
      ])
    );
    return form.value.member_company_ids
      .map((id) => names.get(id))
      .filter((name): name is string => name !== undefined);
  });

  const generatedName = computed(() =>
    defaultMandateName(memberNames.value, mandateDevices.value)
  );

  /** A preview is debouncing or in flight: its blockers are not the current form's yet. */
  const previewBusy = computed(
    () => previewPending.value || previewLoading.value
  );

  const canSave = computed(
    () =>
      canEdit.value &&
      form.value.name.trim() !== "" &&
      form.value.member_company_ids.length > 0 &&
      !formInvalid.value &&
      blockers.value.length === 0
  );

  /**
   * Whether the companies and devices let a new mandate's wizard continue: companies with no blocker
   * the preview reports (a new mandate's blockers are all about its companies and devices), and in
   * allowlist mode at least one device. Held from a form change until its preview answers, so a
   * blocker it is about to report cannot be stepped past.
   */
  const membersReady = computed(
    () =>
      !previewBusy.value &&
      form.value.member_company_ids.length > 0 &&
      blockers.value.length === 0 &&
      (form.value.device_scope !== "allowlist" ||
        form.value.allowlist_device_ids.length > 0)
  );

  /** Whether the listing lets a new mandate's wizard continue: one the write routes accept, previewed. */
  const listingReady = computed(() => !previewBusy.value && !formInvalid.value);

  const saveErrorMessage = computed(() =>
    saveError.value === null ? null : forwardsErrorMessage(saveError.value)
  );

  /** Whether `?force=1` would change the outcome of the last refused save — admins only (DSP-03: keyed off `error_type`). */
  const canForceSave = computed(
    () =>
      (capabilities.value?.is_admin ?? false) &&
      forwardsErrorType(saveError.value) === FEASIBILITY_BLOCK_ERROR_TYPE
  );

  // The preview and the candidate devices each let only their own latest request answer, so a
  // slow reply for an older form never lands.
  let previewGeneration = 0;
  let candidateGeneration = 0;

  /** Reads the opened mandate into the form; a new mandate's blank form needs nothing read. */
  const load = async () => {
    if (mandateId.value === null) return;
    loading.value = true;
    try {
      await readMandate(mandateId.value);
    } catch (caught) {
      loadError.value = caught;
      notFound.value = isNotFoundError(caught);
      reportUnexpected(caught);
    } finally {
      loading.value = false;
    }
  };

  /**
   * Reads the mandate again after an archive or unarchive, which change its capabilities and status.
   * The last save's warnings belong to the mandate as it was. A failed read leaves what is on screen.
   */
  const reload = async () => {
    warnings.value = [];
    try {
      await readMandate(requireId());
    } catch (caught) {
      loadError.value = caught;
      reportUnexpected(caught);
    }
  };

  /**
   * The heading, the membership baseline and the capabilities read `detail`, which the write
   * response does not carry. Not `reload()`: that clears the warnings the view shows after a save.
   * A failed refetch leaves the stale detail in place; the save itself has already succeeded.
   */
  const refreshDetail = async (id: number) => {
    try {
      detail.value = (await forwardsApi.getMandate(id)).data;
    } catch (caught) {
      loadError.value = caught;
      reportUnexpected(caught);
    }
  };

  /**
   * Asks the backend what the unsaved form would mean. Errors are swallowed: the preview is a hint.
   *
   * A form with a field error is not sent at all, so the last preview stays on screen while the
   * user fixes it rather than being replaced by nothing (D-TN04b-02). A form without companies is
   * not sent either: there is nothing to preview, and the backend would only answer with the
   * missing-company blocker the members picker already states — on a new mandate, before the user
   * has picked anything.
   */
  const refreshPreview = async () => {
    previewPending.value = false;
    if (!canEdit.value || formInvalid.value) return;
    if (form.value.member_company_ids.length === 0) {
      previewGeneration++;
      previewLoading.value = false;
      preview.value = null;
      return;
    }

    const generation = ++previewGeneration;
    previewLoading.value = true;
    try {
      const response = await forwardsApi.previewMandate(
        requestFromForm(form.value),
        mandateId.value
      );
      if (generation === previewGeneration) preview.value = response.data;
    } catch {
      if (generation === previewGeneration) preview.value = null;
    } finally {
      if (generation === previewGeneration) previewLoading.value = false;
    }
  };

  const schedulePreview = debounce(() => {
    void refreshPreview();
  }, PREVIEW_DEBOUNCE_MS);

  /**
   * Loads the devices the form's companies could sell from.
   *
   * A saved mandate whose membership the form has not touched already carries its device rows in the
   * detail response, so only an unsaved mandate or an edited membership costs a request — one per
   * membership change, debounced.
   *
   * Errors are swallowed like the preview's: the panel then simply lists nothing, which is also
   * what an empty membership means.
   */
  const refreshCandidateDevices = async () => {
    const generation = ++candidateGeneration;
    if (!isNew.value && !membershipEdited.value) {
      devicesLoading.value = false;
      return;
    }
    const companyIds = [...form.value.member_company_ids];
    if (companyIds.length === 0) {
      candidateDevices.value = [];
      devicesLoading.value = false;
      return;
    }

    devicesLoading.value = true;
    try {
      const response = await forwardsApi.listCandidateDevices(companyIds);
      if (generation === candidateGeneration)
        candidateDevices.value = response.data;
    } catch {
      if (generation === candidateGeneration) candidateDevices.value = [];
    } finally {
      if (generation === candidateGeneration) devicesLoading.value = false;
    }
  };

  const scheduleCandidateDevices = debounce(() => {
    void refreshCandidateDevices();
  }, PREVIEW_DEBOUNCE_MS);

  /**
   * Loads the companies the session may put in a mandate, once per editing session: the choices do not
   * depend on the mandate. A failure leaves the picker with the mandate's own members only.
   */
  const loadCandidateCompanies = async () => {
    try {
      candidateCompanies.value = (
        await forwardsApi.listCandidateCompanies()
      ).data;
      candidateCompaniesError.value = null;
    } catch (caught) {
      candidateCompaniesError.value = caught;
      reportUnexpected(caught);
    }
  };

  /**
   * Creates or replaces the mandate.
   *
   * @returns the saved mandate's id, or null when the save was refused.
   */
  const save = async (
    options: { force?: boolean } = {}
  ): Promise<number | null> => {
    saving.value = true;
    saveError.value = null;
    try {
      const request = requestFromForm(form.value);
      const existingId = mandateId.value;
      const response =
        existingId === null
          ? await forwardsApi.createMandate(request)
          : await forwardsApi.updateMandate(existingId, request, options);
      mandateId.value = response.data.mandate.id;
      warnings.value = response.data.warnings;
      savedForm.value = formFromMandate(response.data.mandate);
      form.value = formFromMandate(response.data.mandate);
      // A create closes the editor, so only an update reads the detail back.
      if (existingId !== null) await refreshDetail(existingId);
      return response.data.mandate.id;
    } catch (caught) {
      saveError.value = caught;
      reportUnexpected(caught);
      return null;
    } finally {
      saving.value = false;
    }
  };

  /** The user's own name, which no generated one replaces from now on. */
  const setName = (name: string) => {
    nameEdited.value = true;
    form.value.name = name;
  };

  /** Selects a listing kind; each kind returns on the width it was last on. */
  const setListingKind = (kind: MandateListingKind) => {
    form.value.listing_kind = kind;
    if (kind !== "pay_as_produced") form.value.last_listed_kind = kind;
  };

  /** Selects a width of the listed kind — from pay-as-produced, of the kind it was entered from. */
  const setEntryWidth = (width: PeriodWidth) => {
    const kind = form.value.last_listed_kind;
    form.value.listing_kind = kind;
    form.value.entry_widths[kind] = width;
  };

  const discard = () => {
    form.value = cloneForm(savedForm.value);
    nameEdited.value = mandateId.value !== null;
    saveError.value = null;
  };

  /** @returns true when the mandate was deleted. */
  const remove = async (): Promise<boolean> =>
    runAction("delete", () => forwardsApi.deleteMandate(requireId()));

  /** @returns true when the mandate was archived. */
  const archive = async (): Promise<boolean> =>
    runAction("archive", () => forwardsApi.archiveMandate(requireId()));

  /** @returns true when the mandate was unarchived. */
  const unarchive = async (): Promise<boolean> =>
    runAction("unarchive", () => forwardsApi.unarchiveMandate(requireId()));

  // lodash timers are not Vue effects, so ending the session would leave a pending call to fire
  // after the editor is gone.
  onScopeDispose(() => {
    schedulePreview.cancel();
    scheduleCandidateDevices.cancel();
  });

  watch(
    form,
    () => {
      previewPending.value = true;
      schedulePreview();
    },
    { deep: true }
  );

  // A mandate without companies has nothing to name yet; a name in the form then would only mark the
  // untouched wizard as changed.
  watch(generatedName, (name) => {
    if (!nameEdited.value && form.value.member_company_ids.length > 0)
      form.value.name = name;
  });

  // Only the membership decides the candidate devices, so the panel does not refetch on every
  // keystroke in the rest of the form.
  watch(
    () => [...form.value.member_company_ids].sort().join(","),
    () => {
      pruneAllowlistToMembers();
      devicesLoading.value =
        (isNew.value || membershipEdited.value) &&
        form.value.member_company_ids.length > 0;
      scheduleCandidateDevices();
    }
  );

  /**
   * Drops the devices of companies the form no longer names from the allowlist.
   *
   * A dropped company's rows leave the panel with it, so an id left behind could not be unticked
   * and the save stayed blocked on "not registered devices of this mandate's companies" with nothing
   * to click (RV-mandates-08). Ids of devices this editor has never seen are kept: they belong to the
   * saved mandate and are not this membership change's to discard.
   */
  function pruneAllowlistToMembers() {
    const members = new Set(form.value.member_company_ids);
    // Every row the editor has seen, not only the ones on show: an edited membership swaps the
    // panel to the candidate rows, and the saved mandate's rows are what the allowlist was built from.
    const seen = [...(detail.value?.devices ?? []), ...candidateDevices.value];
    const dropped = new Set(
      seen
        .filter((device) => !members.has(device.company_id))
        .map((device) => device.device_id)
    );
    if (dropped.size === 0) return;

    const kept = form.value.allowlist_device_ids.filter(
      (deviceId) => !dropped.has(deviceId)
    );
    if (kept.length !== form.value.allowlist_device_ids.length)
      form.value.allowlist_device_ids = kept;
  }

  async function readMandate(id: number) {
    const [detailResponse, commitmentsResponse] = await Promise.all([
      forwardsApi.getMandate(id),
      forwardsApi.listMandateCommitments(id),
    ]);
    detail.value = detailResponse.data;
    commitments.value = commitmentsResponse.data;
    form.value = formFromMandate(detailResponse.data.mandate);
    savedForm.value = formFromMandate(detailResponse.data.mandate);
    loadError.value = null;
  }

  function reportUnexpected(error: unknown) {
    if (!isDomainError(error)) globalErrorHandler(error);
  }

  function requireId(): number {
    if (mandateId.value === null)
      throw new Error("The forward sales mandate has not been saved yet.");
    return mandateId.value;
  }

  async function runAction(
    kind: "delete" | "archive" | "unarchive",
    call: () => Promise<unknown>
  ): Promise<boolean> {
    busyAction.value = kind;
    saveError.value = null;
    try {
      await call();
      return true;
    } catch (caught) {
      saveError.value = caught;
      reportUnexpected(caught);
      return false;
    } finally {
      busyAction.value = null;
    }
  }

  return reactive({
    mandateId: readonly(mandateId),
    form,
    // Not wrapped in `readonly()`: these are deep API payloads and `DeepReadonly` would force a
    // cast at every component boundary that only reads them anyway.
    detail,
    commitments,
    preview,
    candidateDevices,
    /** The form as last saved, or blank for a mandate not saved yet. */
    savedForm: computed(() => savedForm.value),
    loading: readonly(loading),
    previewLoading: readonly(previewLoading),
    /** The device panel's rows are being read for a changed membership; stale ones may still show. */
    devicesLoading: readonly(devicesLoading),
    previewBusy,
    saving: readonly(saving),
    busyAction: readonly(busyAction),
    // Computeds rather than `readonly()`: these hold foreign error objects, which a readonly
    // proxy would wrap and so break identity checks and `instanceof` on.
    loadError: computed(() => loadError.value),
    notFound: readonly(notFound),
    nameEdited: readonly(nameEdited),
    saveError: computed(() => saveError.value),
    candidateCompaniesError: computed(() => candidateCompaniesError.value),
    saveErrorMessage,
    warnings: readonly(warnings),
    isNew,
    isDirty,
    /** The listing differs from the saved mandate's (always, for a mandate not saved yet). */
    listingChanged,
    entryWidth,
    recurringDraft,
    oneOffListings,
    formInvalid,
    minimumPriceProblem,
    productionRangeProblem,
    canEdit,
    membersLocked,
    offersOwnCompaniesOnly,
    canForceSave,
    capabilities,
    blockers,
    previewWarnings,
    devices,
    mandateDevices,
    memberCandidates,
    memberNames,
    canSave,
    membersReady,
    listingReady,
    escrowRemaining,
    load,
    reload,
    loadCandidateCompanies,
    refreshPreview,
    refreshCandidateDevices,
    save,
    setName,
    setListingKind,
    setEntryWidth,
    discard,
    remove,
    archive,
    unarchive,
  });
}

function sortedIds(ids: readonly number[]): number[] {
  return [...ids].sort((left, right) => left - right);
}
